// Wrecking Yard — self-contained box3d runtime for the /wrecking-yard page.
//
// Phase 3b: ported onto the three-box3d library (`box3d-web`). The hand-rolled
// bridge + inline accumulator/sleep-sweep/radial-impulse are replaced by the
// library's World API + FixedStepper / SleepManager / radialImpulse helpers, and
// the drag "hand" now releases via destroyJoint (retiring the destroy-the-hand-
// body hack). The runtime's public shape (rt.transforms flat layout, brick/chain/
// ball/cannonball arrays, offset helpers, rt.caps, __yardState/__yardDebug) is
// unchanged so the render layer and E2E bridges are untouched.

import {
  createBox3D,
  probeCapabilities,
  FixedStepper,
  TransformBuffer,
  SleepManager,
  applyArticulatedPose,
  radialImpulse,
  type Box3D,
  type World,
  type BodyHandle,
  type JointHandle,
  type ArticulatedPose,
} from 'box3d-web';

export const YARD_FIXED_DT = 1 / 60;
// 4 substeps: 2 was measured cheaper per step but the settled stack never
// falls asleep (perpetual micro-jitter keeps ~all bricks awake), so 4 wins on
// real cost — structures are also force-slept right after spawn.
//
// Invariant (Phase 1a): substeps are ALWAYS YARD_NORMAL_SUBSTEPS (4). In box3d,
// world.step(dt, substeps) uses substeps as the contact-solver iteration count
// (not a free "work budget" knob). Dropping below 4 during the contact-heavy
// /sleeping-city collapse left ~4000-brick pileups under-converged — towers
// bounced/jittered instead of collapsing (observed regression). Only the
// FixedStepper catch-up budget (maxStepsPerFrame) may adapt under load.
//
// Phase 1b: maxSteps is coverage+lifecycle-protected. Pre-gate maxSteps=1
// (substeps held at 4) reached only 0.40–0.4667 headless coverage in 90s;
// pre-gate maxSteps=2 still failed unchanged at 0.5333 in 90s. Only pre-gate
// maxSteps=3 preserves the standing coverage contract — no early catch-up
// reduction before the gate. Post-gate overload may still drop maxSteps to 1
// only while cascadeOptimizationActive is true.
export const YARD_SUBSTEPS = 4;
/** Solver substeps passed to world.step — always 4 (contact-solver iterations). */
export const YARD_NORMAL_SUBSTEPS = 4;
/** Normal FixedStepper catch-up budget. */
export const YARD_NORMAL_MAX_STEPS = 3;
/**
 * Pre-gate / post-cascade overload FixedStepper catch-up budget. Equals normal
 * maxSteps (3): pre-gate maxSteps=1 reached 0.40–0.4667 headless coverage in 90s;
 * pre-gate maxSteps=2 still failed unchanged at 0.5333 in 90s. Only 3 preserves
 * the standing coverage contract — kept as a named constant to document/gate that
 * decision even though it equals YARD_NORMAL_MAX_STEPS. Also used when coverage
 * is sticky but cascadeOptimizationActive is false (post-cascade re-settle).
 */
export const YARD_PROPAGATION_MAX_STEPS = 3;
/** Active post-gate overload FixedStepper catch-up budget (prefer dilation over thrash). */
export const YARD_OVERLOAD_MAX_STEPS = 1;
/** Awake brick count above which maxSteps may drop (substeps stay at 4). */
export const YARD_OVERLOAD_AWAKE_BRICK_THRESHOLD = 500;
/** Last stepped-frame stepMs (ms) above which maxSteps may drop (coverage+lifecycle gated). */
export const YARD_OVERLOAD_STEP_MS_THRESHOLD = 12;
/**
 * Product wake-coverage gate (sticky everAwake / brickCount). Matches the
 * standing sleeping-city / cascade headless contract — not a free tuning knob.
 * Sticky: once true it stays true, so aggressive maxSteps must also require
 * cascadeOptimizationActive to avoid post-cascade micro-jitter deadlock.
 */
export const YARD_WAKE_COVERAGE_THRESHOLD = 0.75;

/** Per-frame physics budget selected by selectYardStepBudget. */
export interface YardStepBudget {
  substeps: number;
  maxStepsPerFrame: number;
}

/**
 * Sticky ever-awake fraction vs product wake gate. Zero bricks => false.
 */
export function isYardWakeCoverageSatisfied(everAwakeCount: number, brickCount: number): boolean {
  return brickCount > 0 && everAwakeCount / brickCount >= YARD_WAKE_COVERAGE_THRESHOLD;
}

/**
 * Pure Phase 1a/1b budget policy.
 *
 * Invariant: substeps are ALWAYS YARD_NORMAL_SUBSTEPS (4). In box3d,
 * world.step(dt, substeps) uses substeps as contact-solver iterations; dropping
 * below 4 during the contact-heavy /sleeping-city collapse under-converges
 * pileups (spongy/bouncy stacks — observed regression). Only maxStepsPerFrame
 * adapts under load via the FixedStepper catch-up budget.
 *
 * Aggressive maxSteps reduction requires BOTH wakeCoverageSatisfied AND
 * cascadeOptimizationActive. Coverage is sticky (everAwake never clears), so
 * lifecycle scoping prevents the measured post-cascade 3625-awake micro-jitter
 * deadlock from remaining on a reduced catch-up budget after the cascade ends.
 * - substeps: always YARD_NORMAL_SUBSTEPS (4)
 * - maxStepsPerFrame when overload (awake > 500 OR lastStepMs > 12):
 *     YARD_PROPAGATION_MAX_STEPS (3) while either gate is false
 *       — identical to normal; no early catch-up reduction pre-gate / post-cascade
 *     1 only when both gates are true (active post-gate overload)
 *   else 3 (normal)
 * Key states: pre-gate active high {4,3}; post-gate active high {4,1};
 * post-gate inactive high {4,3}; post-gate inactive step spike {4,3};
 * active post-gate low-awake step spike {4,1}; normal {4,3}.
 * Callers pass previous-frame awake brick count, last stepMs, whether the
 * product wake gate has been secured, and whether cascade lifecycle is active.
 * History: adaptive substeps 4→1 mid-cascade regressed collapse quality; only
 * maxSteps adapts. Pre-gate maxSteps=1 (substeps=4) → 0.40–0.4667 in 90s;
 * pre-gate maxSteps=2 still failed at 0.5333; only maxSteps=3 preserves coverage.
 */
export function selectYardStepBudget(
  awakeBrickCount: number,
  lastStepMs: number,
  wakeCoverageSatisfied: boolean,
  cascadeOptimizationActive = false,
): YardStepBudget {
  const awakeOverload = awakeBrickCount > YARD_OVERLOAD_AWAKE_BRICK_THRESHOLD;
  const stepOverload = lastStepMs > YARD_OVERLOAD_STEP_MS_THRESHOLD;
  const overload = awakeOverload || stepOverload;
  // Sticky coverage must not keep aggressive maxSteps after cascade ends.
  const aggressiveAllowed = wakeCoverageSatisfied && cascadeOptimizationActive;
  return {
    substeps: YARD_NORMAL_SUBSTEPS,
    maxStepsPerFrame: overload
      ? aggressiveAllowed
        ? YARD_OVERLOAD_MAX_STEPS
        : YARD_PROPAGATION_MAX_STEPS
      : YARD_NORMAL_MAX_STEPS,
  };
}

// --- Phase 2: cascade debris retirement (dynamic → static) + wake-back ------
// Settled cascade rubble becomes static so it leaves the mega-island solve.
// Yard bricks spawn dynamic density=3; setBodyType dynamic→static→dynamic was
// probed to restore mass (~0.194 kg) and gravity (matches never-flipped control).
// Demolition's density-0 static spawn path is a different case — do not copy it.
// Feature-gated on caps.setBodyType; no-op keeps Phase 1 behavior.
//
// Retirement runs ONLY after the cascade lifecycle ends (cascadeOptimizationActive
// false). Retiring during the active wave welded lower tower layers (y ≤ 1.35
// covers the bottom ~4 of 22 cascade layers) to the world before the wave
// reached them — towers then could not topple.
//
// Stillness signal: engine sleep (brickSleepStates, plus SleepManager <1cm/2s
// sweep) is trustworthy. The per-frame L1 ≤ 0.015 m displacement check alone
// accepts up to ~0.9 m/s at 60fps as "still" and ignores rotation — so
// engine-awake bricks must never accumulate dwell even when grounded + slow.
//
// Explicit retirement classification (Phase 2 pure policy — no hidden branch):
//   ground   — engine-asleep, y ≤ GROUND_Y, everAwake+slow, 1.25s dwell.
//              Only after cascade ends; settled floor trail leaves the island.
//   elevated — classified for diagnostics, but never retired. Low displacement
//              cannot prove structural support; pinning these bodies can leave
//              tower sections suspended when supporting rubble moves later.
//   none     — blocked (motion / engine-awake / untouched / elevated).
// Disp bands: ground and elevated use L1 ≤ 0.015 (secondary; engine sleep gates).
/** Ground / pile Y gate (matches soft sleep groundY). */
export const YARD_RETIRE_GROUND_Y = 1.35;
/** Ground per-step L1 displacement (m) below which dwell accumulates. */
export const YARD_RETIRE_DISP_EPS = 0.015;
/**
 * Rubble-scale dwell (s) for ground tier before dynamic→static.
 * Floor trail behind the wave uses a conservative continuous-settle window.
 */
export const YARD_RETIRE_DWELL_SEC = 1.25;
/**
 * Elevated L1 displacement (m). Matches ground (0.015) within the plan's
 * 0.01–0.02 band so low-amplitude supported-pile jitter can accumulate dwell;
 * a tighter 0.006 stranded ~152 post-cascade awake on supported rubble.
 */
export const YARD_RETIRE_ELEVATED_DISP_EPS = 0.015;
/**
 * Retained elevated-tier threshold for policy diagnostics. The safety gate
 * currently blocks elevated retirement entirely.
 */
export const YARD_RETIRE_ELEVATED_DWELL_SEC = 2.5;
/**
 * After wake-back, dwell is set to -grace so retirement cannot fire same-frame
 * (or until the brick has been dynamic long enough to re-settle).
 */
export const YARD_RETIRE_WAKE_GRACE_SEC = 0.5;
/** Contact approach speed that can wake a retired brick via brick↔dynamic hit. */
export const YARD_RETIRE_WAKE_CONTACT_SPEED = 2.2;

/**
 * Retirement stillness tier for this physics step (pure policy).
 * Explicit classification model — safety gate is applied by {@link gateRetireTier}.
 */
export type YardRetireTier = 'none' | 'ground' | 'elevated';

/**
 * Classify this step's retirement tier from pose + engine sleep (per-brick).
 * Callers must still apply {@link gateRetireTier}; elevated classification is
 * diagnostic only and may not accumulate dwell or retire.
 * - asleep=false → always 'none' (engine-awake never dwells; sleep is the
 *   trustworthy stillness signal — per-frame 0.015 m L1 alone reads ~0.9 m/s
 *   at 60fps as still and ignores rotation)
 * - everAwake=false → always 'none' (untouched pre-cascade base never retires)
 * - y ≤ ground gate + slow → 'ground'
 * - y > ground + slow → 'elevated'
 * - otherwise 'none' (moving)
 */
export function classifyRetireTier(
  y: number,
  disp: number,
  everAwake: boolean,
  asleep = true,
): YardRetireTier {
  // Engine sleep (plus SleepManager <1cm/2s) is the trustworthy stillness signal.
  if (!asleep || !everAwake) return 'none';
  if (y <= YARD_RETIRE_GROUND_Y) {
    return disp <= YARD_RETIRE_DISP_EPS ? 'ground' : 'none';
  }
  return disp <= YARD_RETIRE_ELEVATED_DISP_EPS ? 'elevated' : 'none';
}

/**
 * Safety gate for Phase 2 retirement.
 * Only ground/pile debris may become static. Elevated low-motion bodies remain
 * dynamic because displacement alone cannot prove they are permanently supported.
 */
export function gateRetireTier(
  tier: YardRetireTier,
  _everAwakeCount: number,
  _brickCount: number,
): YardRetireTier {
  void _everAwakeCount;
  void _brickCount;
  if (tier === 'ground') return 'ground';
  return 'none';
}

/** Dwell seconds required for the given tier; Infinity for 'none'. */
export function retireDwellThresholdForTier(tier: YardRetireTier): number {
  if (tier === 'ground') return YARD_RETIRE_DWELL_SEC;
  if (tier === 'elevated') return YARD_RETIRE_ELEVATED_DWELL_SEC;
  return Number.POSITIVE_INFINITY;
}

/**
 * Pure dwell accumulator for retirement classification.
 * - retired: frozen (no re-accumulate)
 * - dwell < 0: wake-back grace counts up toward 0 regardless of tier
 * - tier 'ground'|'elevated': accumulate dt toward that tier's threshold
 * - tier 'none': reset to 0 (after grace) — motion / pre-gate elevated / untouched
 */
export function updateRetireDwell(
  currentDwell: number,
  opts: {
    tier: YardRetireTier;
    retired: boolean;
    dt: number;
  },
): number {
  if (opts.retired) return currentDwell;
  if (currentDwell < 0) {
    return Math.min(0, currentDwell + opts.dt);
  }
  if (opts.tier === 'none') return 0;
  return currentDwell + opts.dt;
}

/**
 * True when dwell has reached the tier threshold and the brick is still dynamic.
 * Ground uses 1.25s. Elevated remains safety-gated to `none`.
 */
export function shouldRetireFromDwell(
  dwell: number,
  retired: boolean,
  tier: YardRetireTier,
): boolean {
  if (retired || tier === 'none') return false;
  return dwell >= retireDwellThresholdForTier(tier);
}

/**
 * True only when the brick body ID sequence is identical in length and order.
 * Length-only equality is insufficient: structure reset/rebuild often replaces
 * N old handles with N new handles, and stale everAwake/retired/dwell must not
 * survive onto fresh dynamic bodies. Cannonball churn leaves brick IDs unchanged.
 */
export function yardBrickSetUnchanged(
  prevBrickBodyIds: ArrayLike<number>,
  nextBrickBodies: readonly number[],
): boolean {
  const n = nextBrickBodies.length;
  if (prevBrickBodyIds.length !== n) return false;
  for (let i = 0; i < n; i += 1) {
    if (prevBrickBodyIds[i] !== nextBrickBodies[i]) return false;
  }
  return true;
}

/**
 * Pure contact wake-back gate for a retired cascade brick.
 * Actors (ball / chain / cannonball) always wake; other partners only when
 * approach speed is meaningful — resting zero-speed contacts must not re-inflate
 * the island.
 */
export function shouldWakeRetiredOnContact(opts: {
  brickRetired: boolean;
  otherIsActor: boolean;
  otherIsDynamicPartner: boolean;
  approachSpeed: number;
}): boolean {
  if (!opts.brickRetired) return false;
  if (opts.otherIsActor) return true;
  return opts.otherIsDynamicPartner && opts.approachSpeed >= YARD_RETIRE_WAKE_CONTACT_SPEED;
}

export const MAX_BRICKS = 1400;
export const SLEEPING_CITY_BRICKS = 4000;
export const MAX_CANNONBALLS = 8;
export const CHAIN_LINKS = 8;
export const BALL_RADIUS = 1.15;
// Hero ball mass stays at the cascade-proven dens=25 (~160 kg).
export const BALL_DENSITY = 25;
// Links: modest fatten over foil (was r=0.09 dens=18 → 0.26 kg / ~620:1).
// Full ~20:1 mass ratio starves the cascade wave; dens~24 / r=0.1 (~0.45 kg,
// ~350:1) + damping is the measured sweet spot (cascade ≥0.75, cannon peak ≪30).
export const LINK_RADIUS = 0.1;
export const LINK_HALF_HEIGHT = 0.22;
export const LINK_DENSITY = 24;
// Damping is the primary whip fix; too high (≥0.4 lin) drops cascade coverage.
export const LINK_LINEAR_DAMPING = 0.2;
export const LINK_ANGULAR_DAMPING = 0.4;
export const BALL_LINEAR_DAMPING = 0.03;
// Keep the hero ball's visual spin from lingering after off-centre impacts.
// The explicit calm/reset paths also zero angular velocity, but this damping
// gives free-play cannon hits a finite, readable decay instead of a long tail.
export const BALL_ANGULAR_DAMPING = 0.3;
// Cannonball: dense iron-like sphere sized to read on camera. Mass ≈ density·(4/3)πr³
// ≈ 9.7 kg vs brick ≈ 0.19 kg (~50×). Honest ballistic spawn from camera; high speed
// keeps gravity drop small over a 25–35 unit flight (visual mesh radius matches collider).
export const CANNONBALL_RADIUS = 0.38;
export const CANNONBALL_DENSITY = 42;
export const CANNONBALL_SPEED = 58;
/** Retired bricks near the ballistic path are restored before impact resolution. */
export const CANNON_WAKE_CORRIDOR_RADIUS = 1.05;
export const CANNON_WAKE_CORRIDOR_LENGTH = 80;
export const BRICK_FULL = { x: 0.45, y: 0.32, z: 0.45 };
// The rest hang is vertical and clear of every cascade tower. The cascade
// launch path lifts the chain to a separate, deterministic strike pose.
export const CHAIN_ANCHOR = { x: 5.9, y: 13.6, z: 0.6 };
export const BOOM_RADIUS = 3.2;

export const SLEEP_HEATMAP_ASLEEP = '#315f8a';
export const SLEEP_HEATMAP_AWAKE = '#ff633f';

/** Stable presentation colors for the Phase 3 sleep-state heatmap. */
export function getSleepHeatmapColor(awake: boolean): string {
  return awake ? SLEEP_HEATMAP_AWAKE : SLEEP_HEATMAP_ASLEEP;
}

/** Capsule volume (cylinder 2h + two hemisphere caps) for mass ratio checks. */
export function capsuleVolume(radius: number, halfHeight: number): number {
  return 2 * Math.PI * radius * radius * halfHeight + (4 / 3) * Math.PI * radius * radius * radius;
}

/** Sphere volume for mass ratio checks. */
export function sphereVolume(radius: number): number {
  return (4 / 3) * Math.PI * radius * radius * radius;
}

const BX = 0.46; // horizontal grid pitch (brick + ~10mm clearance)
const BY = 0.3235; // layer pitch (brick 0.32 + ~3mm vertical gap)
const CHAIN_SPACING = 0.55;
const HAND_MAX_SPEED = 28;
const BRICK_SLEEP_SWEEP_INTERVAL = 2;
const BRICK_SLEEP_MOVE_EPS = 0.01;

export type YardTool = 'ball' | 'cannon' | 'boom' | 'grab';
export type YardStructure = 'tower' | 'castle' | 'colosseum' | 'city' | 'cascade';

export interface YardImpact {
  x: number;
  y: number;
  z: number;
  strength: number;
}

export interface YardCaps {
  explode: boolean;
  castRayClosest: boolean;
  awakeBodyCount: boolean;
  bodyCount: boolean;
  setBodyType: boolean;
  getLinearVelocity: boolean;
  setAwake: boolean;
  readSleepStates: boolean;
}

interface BrickSpec {
  x: number;
  y: number;
  z: number;
  qy: number;
  qw: number;
  tint: string;
}

export interface YardRuntime {
  world: World;
  caps: YardCaps;
  stepper: FixedStepper;
  buffer: TransformBuffer;
  sleep: SleepManager;
  structure: YardStructure;
  structureVersion: number;
  slowMo: boolean;
  fixedTime: number;
  stepMs: number;
  awakeCount: number;
  /**
   * Count of bricks with sleep-state === 1 from the last readBrickSleepStates pass.
   * Used as the previous-frame input to selectYardStepBudget (no second brick scan).
   */
  awakeBrickCount: number;
  /** Actual substeps passed to world.step on the most recent advance policy select. */
  substepsUsed: number;
  /** Actual FixedStepper maxStepsPerFrame applied on the most recent advance. */
  maxStepsUsed: number;
  bodyCountTotal: number;
  brickBodies: number[];
  brickColors: string[];
  chainBodies: number[];
  chainJoints: JointHandle[];
  ballBodyId: number;
  cannonballs: number[];
  handBodyId: number;
  dragJoint: JointHandle | 0;
  handPos: { x: number; y: number; z: number };
  dragTarget: { x: number; y: number; z: number };
  dragging: boolean;
  bodyIds: Int32Array;
  brickBodyIds: Int32Array;
  brickSleepStates: Uint8Array;
  /** Sticky OR of brick sleep-states (1 once a brick has ever been awake). */
  brickEverAwake: Uint8Array;
  /** Count of bricks that have been awake at least once since last rebuild. */
  everAwakeCount: number;
  /**
   * Phase 2: 1 when cascade brick i is retired (static debris). Aligned with
   * brickBodies indices; rebuilt/reset with the brick set. CASCADE rebuild leaves
   * all bricks dynamic (zeros).
   */
  brickRetired: Uint8Array;
  /**
   * Phase 2: slow grounded dwell (sim-seconds). Negative = wake-back grace.
   * Aligned with brickBodies. SleepManager still watches all brick handles
   * (library sweep may sleepBody statics — harmless no-op); app force-sleep
   * helpers skip retired indices. Statics are out of the dynamic island.
   */
  brickRetireDwell: Float32Array;
  /** Count of retired (static) cascade bricks. */
  retiredCount: number;
  /** Count of still-dynamic bricks (brickBodies.length - retiredCount). */
  dynamicBrickCount: number;
  transforms: Float32Array;
  prevTransforms: Float32Array;
  prevValid: boolean;
  bodyIndex: Map<number, number>;
  idsDirty: boolean;
  impacts: YardImpact[];
  staticBodyCount: number;
  bodyLimit: number;
  profileKey: string;
  /** Peak chain-link linear speed (m/s) since last resetChainStability. */
  chainPeakSpeed: number;
  /** Peak chain-link frame-to-frame displacement (m) since last reset. */
  chainPeakDisp: number;
  /** Frames since last reset where any link speed was sampled. */
  chainSampleFrames: number;
  /** Last frame max link speed (m/s). */
  chainLastMaxSpeed: number;
}

let modulePromise: Promise<Box3D> | null = null;
let runtime: YardRuntime | null = null;
let runtimePromise: Promise<YardRuntime> | null = null;
let runtimePromiseKey: string | null = null;
let runtimeFailed = false;

function disposeYardRuntime(rt: YardRuntime | null) {
  if (!rt) return;
  try {
    rt.world.destroy();
  } catch {
    // The native world may already have been torn down by its owner.
  }
}

function loadModule(wasmUrl?: string | URL) {
  if (!modulePromise) {
    // Phase 3 worker path passes an explicit ?url asset so Vite emits/fetches
    // wasm from the worker bundle; main-thread path keeps the library default.
    modulePromise = wasmUrl != null ? createBox3D({ wasmUrl }) : createBox3D();
  }
  return modulePromise;
}

export function getExistingYardRuntime() {
  return runtime;
}

export interface YardRuntimeOptions {
  bodyLimit?: number;
  /**
   * Optional WASM URL override (Phase 3 worker). When set, createBox3D loads
   * from this URL so production worker chunks resolve box3d.wasm correctly.
   */
  wasmUrl?: string | URL;
}

/**
 * Phase 3: Sleeping City scale cascade uses a dedicated physics worker.
 * Normal wrecking-yard profiles (bodyLimit ≤ MAX_BRICKS) stay on the main thread.
 */
export function shouldUseYardPhysicsWorker(
  structure: YardStructure,
  bodyLimit: number,
): boolean {
  // Local import-free gate — same rule as yardWorkerProtocol.shouldUseYardPhysicsWorker.
  return structure === 'cascade' && bodyLimit > MAX_BRICKS;
}

export async function getYardRuntime(structure: YardStructure = 'tower', options: YardRuntimeOptions = {}) {
  const bodyLimit = options.bodyLimit ?? MAX_BRICKS;
  const profileKey = `${structure}:${bodyLimit}`;
  if (runtime?.profileKey === profileKey) return runtime;
  if (runtime) {
    const stale = runtime;
    runtime = null;
    disposeYardRuntime(stale);
  }
  if (runtimeFailed) throw new Error('Yard physics failed to initialize');

  if (!runtimePromise || runtimePromiseKey !== profileKey) {
    runtimePromiseKey = profileKey;
    const pending = createYardRuntime(structure, bodyLimit, options.wasmUrl)
      .then((created) => {
        // A route can switch while WASM initializes; only the latest profile
        // may become the shared runtime used by a subsequently mounted page.
        if (runtimePromiseKey === profileKey) runtime = created;
        else disposeYardRuntime(created);
        return created;
      })
      .catch((error: unknown) => {
        if (runtimePromiseKey === profileKey) {
          runtimeFailed = true;
          runtimePromise = null;
          runtimePromiseKey = null;
        }
        throw error;
      });
    runtimePromise = pending;
  }

  return runtimePromise;
}

async function createYardRuntime(
  structure: YardStructure,
  bodyLimit: number,
  wasmUrl?: string | URL,
): Promise<YardRuntime> {
  const box3d = await loadModule(wasmUrl);
  const world = box3d.createWorld({ gravity: [0, -9.8, 0] });
  const probed = probeCapabilities(world);
  const caps: YardCaps = {
    explode: probed.explode,
    castRayClosest: probed.castRay,
    awakeBodyCount: probed.awakeBodyCount,
    bodyCount: probed.bodyCount,
    setBodyType: probed.setBodyType,
    getLinearVelocity: probed.getLinearVelocity,
    setAwake: probed.setAwake,
    readSleepStates: typeof world.readSleepStates === 'function',
  };

  const rt: YardRuntime = {
    world,
    caps,
    stepper: new FixedStepper({ fixedDt: YARD_FIXED_DT, substeps: YARD_SUBSTEPS, maxStepsPerFrame: 3 }),
    buffer: new TransformBuffer(64),
    sleep: new SleepManager(world, {
      sweepIntervalSec: BRICK_SLEEP_SWEEP_INTERVAL,
      moveThreshold: BRICK_SLEEP_MOVE_EPS,
    }),
    structure,
    structureVersion: 0,
    slowMo: false,
    fixedTime: 0,
    stepMs: 0,
    awakeCount: 0,
    awakeBrickCount: 0,
    substepsUsed: YARD_NORMAL_SUBSTEPS,
    maxStepsUsed: YARD_NORMAL_MAX_STEPS,
    bodyCountTotal: 0,
    brickBodies: [],
    brickColors: [],
    chainBodies: [],
    chainJoints: [],
    ballBodyId: 0,
    cannonballs: [],
    handBodyId: 0,
    dragJoint: 0,
    handPos: { x: 0, y: 0, z: 0 },
    dragTarget: { x: 0, y: 0, z: 0 },
    dragging: false,
    bodyIds: new Int32Array(0),
    brickBodyIds: new Int32Array(0),
    brickSleepStates: new Uint8Array(0),
    brickEverAwake: new Uint8Array(0),
    everAwakeCount: 0,
    brickRetired: new Uint8Array(0),
    brickRetireDwell: new Float32Array(0),
    retiredCount: 0,
    dynamicBrickCount: 0,
    transforms: new Float32Array(0),
    prevTransforms: new Float32Array(0),
    prevValid: false,
    bodyIndex: new Map(),
    idsDirty: true,
    impacts: [],
    staticBodyCount: 0,
    bodyLimit,
    profileKey: `${structure}:${bodyLimit}`,
    chainPeakSpeed: 0,
    chainPeakDisp: 0,
    chainSampleFrames: 0,
    chainLastMaxSpeed: 0,
  };

  addStatics(rt);
  buildWreckingBall(rt);
  buildStructure(rt, structure);
  return rt;
}

function addStatics(rt: YardRuntime) {
  // Ground slab: top face at y=0.
  const ground = rt.world.createBody({ type: 'static', position: [0, -0.6, 0] });
  rt.world.addBox(ground, [60, 0.6, 60], { density: 0, friction: 0.9, restitution: 0.02 });
  rt.staticBodyCount += 1;
}

function buildWreckingBall(rt: YardRuntime) {
  const anchor = rt.world.createBody({
    type: 'static',
    position: [CHAIN_ANCHOR.x, CHAIN_ANCHOR.y, CHAIN_ANCHOR.z],
  });
  rt.world.addBox(anchor, [0.1, 0.1, 0.1], { density: 0, friction: 0.5, restitution: 0 });
  rt.staticBodyCount += 1;

  let prevBody = anchor;
  let prevBottomY = CHAIN_ANCHOR.y;
  for (let i = 0; i < CHAIN_LINKS; i += 1) {
    const centerY = CHAIN_ANCHOR.y - (i + 0.5) * CHAIN_SPACING;
    const link = rt.world.createBody({
      type: 'dynamic',
      position: [CHAIN_ANCHOR.x, centerY, CHAIN_ANCHOR.z],
      ccd: true,
      linearDamping: LINK_LINEAR_DAMPING,
      angularDamping: LINK_ANGULAR_DAMPING,
    });
    rt.world.addCapsule(link, LINK_RADIUS, LINK_HALF_HEIGHT, {
      density: LINK_DENSITY,
      friction: 0.45,
      restitution: 0.02,
    });
    rt.chainJoints.push(
      rt.world.createSphericalJoint(prevBody, link, {
        anchor: [CHAIN_ANCHOR.x, prevBottomY, CHAIN_ANCHOR.z],
      }),
    );
    rt.chainBodies.push(link);
    prevBody = link;
    prevBottomY = CHAIN_ANCHOR.y - (i + 1) * CHAIN_SPACING;
  }

  const ballY = prevBottomY - BALL_RADIUS;
  rt.ballBodyId = rt.world.createBody({
    type: 'dynamic',
    position: [CHAIN_ANCHOR.x, ballY, CHAIN_ANCHOR.z],
    ccd: true,
    linearDamping: BALL_LINEAR_DAMPING,
    angularDamping: BALL_ANGULAR_DAMPING,
  });
  rt.world.addSphere(rt.ballBodyId as BodyHandle, BALL_RADIUS, {
    density: BALL_DENSITY,
    friction: 0.5,
    restitution: 0.08,
  });
  rt.chainJoints.push(
    rt.world.createSphericalJoint(prevBody, rt.ballBodyId as BodyHandle, {
      anchor: [CHAIN_ANCHOR.x, prevBottomY, CHAIN_ANCHOR.z],
    }),
  );
}

// --- structures ---

// 80% terracotta family with gold bands / cream quoins / stone plinth accents,
// matching design-refs/yard-hero.png (no random blues or whites).
const TERRA = ['#b0472f', '#c25a38', '#a03e2a'];
const GOLD = '#d9a53f';
const CREAM = '#e2cfa8';
const STONE = '#6a675f';

function seededUnit(seed: number) {
  const x = Math.sin(seed * 12.9898) * 43758.5453;
  return x - Math.floor(x);
}

function terra(seed: number): string {
  return TERRA[Math.floor(seededUnit(seed) * TERRA.length) % TERRA.length];
}

function buildTowerSpecs(): BrickSpec[] {
  const specs: BrickSpec[] = [];
  const n = 10;
  const layers = 24;
  const half = (n - 1) / 2;

  // Stone base plinth: one ring outside the footprint, 2 courses tall.
  const np = n + 2;
  const halfP = (np - 1) / 2;
  for (let layer = 0; layer < 2; layer += 1) {
    const y = layer * BY + BRICK_FULL.y / 2;
    for (let ix = 0; ix < np; ix += 1) {
      for (let iz = 0; iz < np; iz += 1) {
        if (ix === 0 || ix === np - 1 || iz === 0 || iz === np - 1) {
          specs.push({ x: (ix - halfP) * BX, y, z: (iz - halfP) * BX, qy: 0, qw: 1, tint: STONE });
        }
      }
    }
  }

  for (let layer = 0; layer < layers; layer += 1) {
    const y = layer * BY + BRICK_FULL.y / 2;
    for (let ix = 0; ix < n; ix += 1) {
      for (let iz = 0; iz < n; iz += 1) {
        const edge = ix === 0 || ix === n - 1 || iz === 0 || iz === n - 1;
        if (!edge) continue;
        const alongX = iz === 0 || iz === n - 1;
        const t = alongX ? ix : iz;
        // Window openings: 2x3-brick voids, two per face, three rows.
        const windowRow = (layer >= 6 && layer <= 8) || (layer >= 12 && layer <= 14) || (layer >= 18 && layer <= 20);
        if (windowRow && (t === 2 || t === 3 || t === 6 || t === 7)) continue;
        const corner = (ix === 0 || ix === n - 1) && (iz === 0 || iz === n - 1);
        const band = layer % 6 === 5;
        const tint = corner ? CREAM : band ? GOLD : terra(layer * 131 + ix * 17 + iz * 7);
        specs.push({ x: (ix - half) * BX, y, z: (iz - half) * BX, qy: 0, qw: 1, tint });
      }
    }
  }
  // crenellation: alternating raised blocks, gold
  const yTop = layers * BY + BRICK_FULL.y / 2;
  for (let ix = 0; ix < n; ix += 1) {
    for (let iz = 0; iz < n; iz += 1) {
      const edge = ix === 0 || ix === n - 1 || iz === 0 || iz === n - 1;
      if (edge && (ix + iz) % 2 === 0) {
        specs.push({ x: (ix - half) * BX, y: yTop, z: (iz - half) * BX, qy: 0, qw: 1, tint: GOLD });
      }
    }
  }
  return specs;
}

function buildCastleSpecs(): BrickSpec[] {
  const specs: BrickSpec[] = [];
  const s = 3.7;
  const towerLayers = 16;
  const seen = new Set<string>();
  const push = (x: number, y: number, z: number, tint: string) => {
    const key = `${Math.round(x * 100)}|${Math.round(y * 100)}|${Math.round(z * 100)}`;
    if (seen.has(key)) return;
    seen.add(key);
    specs.push({ x, y, z, qy: 0, qw: 1, tint });
  };

  for (const cx of [-s, s]) {
    for (const cz of [-s, s]) {
      for (let layer = 0; layer < towerLayers; layer += 1) {
        const y = layer * BY + BRICK_FULL.y / 2;
        for (let ix = 0; ix < 4; ix += 1) {
          for (let iz = 0; iz < 4; iz += 1) {
            if (ix === 0 || ix === 3 || iz === 0 || iz === 3) {
              const corner = (ix === 0 || ix === 3) && (iz === 0 || iz === 3);
              const band = layer % 6 === 5;
              const tint = corner ? CREAM : band ? GOLD : terra(layer * 89 + ix * 13 + iz * 5 + cx + cz);
              push(cx + (ix - 1.5) * BX, y, cz + (iz - 1.5) * BX, tint);
            }
          }
        }
      }
      const yTop = towerLayers * BY + BRICK_FULL.y / 2;
      for (let ix = 0; ix < 4; ix += 1) {
        for (let iz = 0; iz < 4; iz += 1) {
          if ((ix === 0 || ix === 3 || iz === 0 || iz === 3) && (ix + iz) % 2 === 0) {
            push(cx + (ix - 1.5) * BX, yTop, cz + (iz - 1.5) * BX, GOLD);
          }
        }
      }
    }
  }

  // connecting walls (kept clear of the tower footprints)
  const wallLayers = 9;
  const from = -s + 2.5 * BX;
  const to = s - 2.5 * BX;
  const count = Math.round((to - from) / BX);
  for (let layer = 0; layer < wallLayers; layer += 1) {
    const y = layer * BY + BRICK_FULL.y / 2;
    const tintFor = (i: number, side: number) =>
      layer === wallLayers - 1 ? GOLD : terra(layer * 71 + i * 11 + side);
    for (let i = 0; i <= count; i += 1) {
      const p = from + i * BX;
      push(p, y, -s, tintFor(i, 1));
      push(p, y, s, tintFor(i, 2));
      push(-s, y, p, tintFor(i, 3));
      push(s, y, p, tintFor(i, 4));
    }
  }
  const yWallTop = wallLayers * BY + BRICK_FULL.y / 2;
  for (let i = 0; i <= count; i += 2) {
    const p = from + i * BX;
    push(p, yWallTop, -s, CREAM);
    push(p, yWallTop, s, CREAM);
    push(-s, yWallTop, p, CREAM);
    push(s, yWallTop, p, CREAM);
  }
  return specs;
}

function buildColosseumSpecs(): BrickSpec[] {
  const specs: BrickSpec[] = [];
  const rx = 4.35;
  const rz = 3.5;
  // n sized so neighboring columns NEVER touch (arc spacing ~0.58 vs brick
  // 0.45): lateral contact merges the whole ring into one physics island, and
  // one grinding contact then keeps all ~800 bricks awake forever.
  const n = 44;
  const layers = 20;
  const pushRing = (ringRx: number, ringRz: number, count: number, ringLayers: number, ruinFrom: number, arches: boolean) => {
    for (let layer = 0; layer < ringLayers; layer += 1) {
      const y = layer * BY + BRICK_FULL.y / 2;
      for (let i = 0; i < count; i += 1) {
        // Arch openings are full-height colonnade slots: partial-height gaps
        // left the columns above them floating at spawn, raining bricks that
        // never settled.
        if (arches && (i % 7 === 3 || i % 7 === 4)) continue;
        if (layer >= ruinFrom && seededUnit(i * 7 + layer * 131 + count) < (layer - ruinFrom + 1) * 0.18) continue; // ruined rim
        const a = (i / count) * Math.PI * 2;
        const x = Math.cos(a) * ringRx;
        const z = Math.sin(a) * ringRz;
        const tx = -Math.sin(a) * ringRx;
        const tz = Math.cos(a) * ringRz;
        const yaw = Math.atan2(-tz, tx);
        const tint = arches && i % 6 === 0 ? CREAM : layer % 6 === 5 ? GOLD : terra(i * 19 + layer * 53 + count);
        specs.push({ x, y, z, qy: Math.sin(yaw / 2), qw: Math.cos(yaw / 2), tint });
      }
    }
  };

  pushRing(rx, rz, n, layers, 15, true); // outer colonnade, ruined rim
  pushRing(3.55, 2.7, 36, 11, 9, false); // lower inner wall, lightly ruined
  return specs;
}

/** Deterministic 4x4 city block: sixteen 5x5 buildings, ten courses each. */
function buildCitySpecs(): BrickSpec[] {
  const specs: BrickSpec[] = [];
  const footprint = 5;
  const layers = 10;
  const centers = [-10.2, -3.4, 3.4, 10.2];
  centers.forEach((cx, bx) => {
    centers.forEach((cz, bz) => {
      for (let layer = 0; layer < layers; layer += 1) {
        const y = layer * BY + BRICK_FULL.y / 2;
        for (let ix = 0; ix < footprint; ix += 1) {
          for (let iz = 0; iz < footprint; iz += 1) {
            const edge = ix === 0 || ix === footprint - 1 || iz === 0 || iz === footprint - 1;
            const tint = edge
              ? (layer + bx + bz) % 5 === 0
                ? GOLD
                : terra(layer * 131 + bx * 17 + bz * 7 + ix * 3 + iz)
              : terra(layer * 97 + bx * 13 + bz * 11 + ix * 5 + iz * 2);
            specs.push({
              x: cx + (ix - (footprint - 1) / 2) * BX,
              y,
              z: cz + (iz - (footprint - 1) / 2) * BX,
              qy: 0,
              qw: 1,
              tint,
            });
          }
        }
      }
    });
  });
  return specs;
}

// --- Wake Wave cascade boulevard ------------------------------------------------
// Hollow 4×4 towers in path order so mobile slice(0, 2000) keeps a coherent prefix.
// Face-to-face gap ≈ 40–50% of tower height so crumbling debris / topple wakes the next.
// Tower 1 sits in the wrecking-ball strike envelope (rest ≈ 5.9, 8.05, 0.6).
// Center-to-center ~4.05 keeps towers close enough that a mid-run wave doesn't stall
// (wider 5.0 spacing reliably died around tower 11 under headless swiftshader).

/** Hollow footprint edge length (bricks per side). */
export const CASCADE_FOOTPRINT = 4;
/** Courses per cascade tower — tall enough that a tip reaches the next face. */
export const CASCADE_LAYERS = 22;
/** Edge-only bricks per course for a CASCADE_FOOTPRINT hollow square. */
export const CASCADE_BRICKS_PER_LAYER = 4 * (CASCADE_FOOTPRINT - 1);
export const CASCADE_BRICKS_PER_TOWER = CASCADE_BRICKS_PER_LAYER * CASCADE_LAYERS;

/**
 * Path-ordered tower centers (serpentine boulevard). Consecutive centers are
 * spaced so face-to-face gap is well under tower height (domino condition).
 * Tower 1 is within pendulum reach of CHAIN_ANCHOR / ball rest.
 * 15 towers × 22 layers × 12 bricks = 3960 (within [3600, 4000]).
 */
export const CASCADE_TOWER_CENTERS: ReadonlyArray<{ readonly x: number; readonly z: number }> = [
  { x: 3.15, z: 0.55 }, // 1 — strike target (ball rest x=5.9, z=0.6)
  { x: -0.9, z: 0.55 },
  { x: -4.95, z: 0.55 },
  { x: -9.0, z: 0.55 },
  { x: -9.0, z: 4.6 }, // turn up
  { x: -4.95, z: 4.6 },
  { x: -0.9, z: 4.6 },
  { x: 3.15, z: 4.6 },
  { x: 7.2, z: 4.6 },
  { x: 8.3, z: 0.55 }, // offset so the vertical wrecking-ball rest stays clear
  { x: 7.2, z: -3.5 },
  { x: 7.2, z: -7.55 },
  { x: 3.15, z: -7.55 }, // left along the south row
  { x: -0.9, z: -7.55 },
  { x: -4.95, z: -7.55 },
];

/** Outer half-extent of a cascade tower (center → outer face). */
export const CASCADE_TOWER_HALF_EXTENT =
  ((CASCADE_FOOTPRINT - 1) / 2) * BX + BRICK_FULL.x / 2;

/** Tower height from ground to top face of the top brick. */
export function cascadeTowerHeight(): number {
  return (CASCADE_LAYERS - 1) * BY + BRICK_FULL.y;
}

/** Face-to-face gap between two cascade tower centers (must stay > 0 at rest). */
export function cascadeFaceGap(a: { x: number; z: number }, b: { x: number; z: number }): number {
  const c2c = Math.hypot(a.x - b.x, a.z - b.z);
  return c2c - 2 * CASCADE_TOWER_HALF_EXTENT;
}

/** Max allowed face-to-face gap as a fraction of tower height (domino bound). */
export const CASCADE_DOMINO_GAP_FRAC = 0.7;

/** Horizontal launch speed applied to the wrecking ball for cascadeLaunch (m/s). */
export const CASCADE_LAUNCH_SPEED = 17;

/**
 * Small, deterministic left swing used to arm the strike. The ball starts
 * tangent to tower 1's upper-right edge while remaining clear of tower 10.
 */
export const CASCADE_LAUNCH_SWING_RAD = 0.2;

/** Nominal wrecking-ball center at chain hang rest (no launch lift). */
export function cascadeBallRestCenter(): { x: number; y: number; z: number } {
  return {
    x: CHAIN_ANCHOR.x,
    y: CHAIN_ANCHOR.y - CHAIN_LINKS * CHAIN_SPACING - BALL_RADIUS,
    z: CHAIN_ANCHOR.z,
  };
}

/** Deterministic pre-launch ball center on the clear left-swing arc. */
export function cascadeLaunchBallPose(): { x: number; y: number; z: number } {
  const length = CHAIN_LINKS * CHAIN_SPACING + BALL_RADIUS;
  const sin = Math.sin(CASCADE_LAUNCH_SWING_RAD);
  const cos = Math.cos(CASCADE_LAUNCH_SWING_RAD);
  return {
    x: CHAIN_ANCHOR.x - sin * length,
    y: CHAIN_ANCHOR.y - cos * length,
    z: CHAIN_ANCHOR.z,
  };
}

/**
 * Capsule orientation for the angled launch arc.  Each link's spherical-joint
 * anchors are ±local-Y from its center, so the local +Y axis must point toward
 * the fixed anchor.  A -Z rotation by the swing angle does exactly that while
 * keeping adjacent link anchors coincident.
 */
export function cascadeLaunchLinkRotation(): [number, number, number, number] {
  const half = CASCADE_LAUNCH_SWING_RAD * 0.5;
  return [0, 0, -Math.sin(half), Math.cos(half)];
}

/**
 * Max sphere-vs-brick AABB penetration of `ball` into one cascade tower's hollow
 * footprint (positive = overlapping). Pure layout geometry for launch tests.
 */
export function cascadeBallTowerPenetration(
  ball: { x: number; y: number; z: number },
  towerCenter: { x: number; z: number },
  radius = BALL_RADIUS,
): number {
  const half = (CASCADE_FOOTPRINT - 1) / 2;
  const hx = BRICK_FULL.x / 2;
  const hy = BRICK_FULL.y / 2;
  const hz = BRICK_FULL.z / 2;
  let maxPen = 0;
  for (let layer = 0; layer < CASCADE_LAYERS; layer += 1) {
    const by = layer * BY + hy;
    for (let ix = 0; ix < CASCADE_FOOTPRINT; ix += 1) {
      for (let iz = 0; iz < CASCADE_FOOTPRINT; iz += 1) {
        const edge =
          ix === 0 || ix === CASCADE_FOOTPRINT - 1 || iz === 0 || iz === CASCADE_FOOTPRINT - 1;
        if (!edge) continue;
        const bx = towerCenter.x + (ix - half) * BX;
        const bz = towerCenter.z + (iz - half) * BX;
        const cx = Math.max(bx - hx, Math.min(ball.x, bx + hx));
        const cy = Math.max(by - hy, Math.min(ball.y, by + hy));
        const cz = Math.max(bz - hz, Math.min(ball.z, bz + hz));
        const pen = radius - Math.hypot(ball.x - cx, ball.y - cy, ball.z - cz);
        if (pen > maxPen) maxPen = pen;
      }
    }
  }
  return maxPen;
}

/**
 * Teleport ball + chain to the deterministic clear launch arc and zero motion.
 * The chain keeps its joint lengths while the launcher is re-armed, so settle
 * drift / tower-10 contacts cannot linger into the strike.
 */
function buildWreckingBallPose(rt: YardRuntime, swingRad: number): ArticulatedPose[] {
  const sin = Math.sin(swingRad);
  const cos = Math.cos(swingRad);
  const rotation = swingRad === 0
    ? [0, 0, 0, 1] as [number, number, number, number]
    : cascadeLaunchLinkRotation();
  const poses: ArticulatedPose[] = rt.chainBodies.map((body, index) => {
    const length = (index + 0.5) * CHAIN_SPACING;
    return {
      body: body as BodyHandle,
      position: [
        CHAIN_ANCHOR.x - sin * length,
        CHAIN_ANCHOR.y - cos * length,
        CHAIN_ANCHOR.z,
      ],
      rotation,
    };
  });

  if (rt.ballBodyId) {
    const length = CHAIN_LINKS * CHAIN_SPACING + BALL_RADIUS;
    poses.push({
      body: rt.ballBodyId as BodyHandle,
      position: [
        CHAIN_ANCHOR.x - sin * length,
        CHAIN_ANCHOR.y - cos * length,
        CHAIN_ANCHOR.z,
      ],
      // A sphere looks unchanged when rotated, but its joint anchor is local-space.
      // It must use the chain rotation or the terminal anchor separates on teleport.
      rotation,
    });
  }
  return poses;
}

function applyWreckingBallPose(rt: YardRuntime, swingRad: number, sleep: boolean) {
  return applyArticulatedPose(rt.world, buildWreckingBallPose(rt, swingRad), {
    joints: rt.chainJoints,
    maxJointAnchorSeparation: 0.001,
    clearVelocities: true,
    sleep,
  });
}

function prepareCascadeLaunchPose(rt: YardRuntime) {
  applyWreckingBallPose(rt, CASCADE_LAUNCH_SWING_RAD, false);
}

/**
 * Restore the connected chain and ball to a coherent, settled vertical pose
 * before a structure reset/rebuild. Cascade tower 10 is offset from the rest
 * envelope so this is both joint-valid and a stable gravity equilibrium.
 */
export function restoreWreckingBallPose(rt: YardRuntime, structure: YardStructure) {
  void structure;
  applyWreckingBallPose(rt, 0, rt.caps.setAwake);
}

/** Deterministic serpentine cascade city: path-ordered hollow towers. */
function buildCascadeSpecs(): BrickSpec[] {
  const specs: BrickSpec[] = [];
  const footprint = CASCADE_FOOTPRINT;
  const layers = CASCADE_LAYERS;
  const half = (footprint - 1) / 2;

  CASCADE_TOWER_CENTERS.forEach((center, ti) => {
    for (let layer = 0; layer < layers; layer += 1) {
      const y = layer * BY + BRICK_FULL.y / 2;
      for (let ix = 0; ix < footprint; ix += 1) {
        for (let iz = 0; iz < footprint; iz += 1) {
          const edge = ix === 0 || ix === footprint - 1 || iz === 0 || iz === footprint - 1;
          if (!edge) continue;
          const corner = (ix === 0 || ix === footprint - 1) && (iz === 0 || iz === footprint - 1);
          const band = layer % 6 === 5;
          const tint = corner ? CREAM : band ? GOLD : terra(layer * 131 + ti * 41 + ix * 17 + iz * 7);
          specs.push({
            x: center.x + (ix - half) * BX,
            y,
            z: center.z + (iz - half) * BX,
            qy: 0,
            qw: 1,
            tint,
          });
        }
      }
    }
  });
  return specs;
}

function structureSpecs(structure: YardStructure): BrickSpec[] {
  if (structure === 'castle') return buildCastleSpecs();
  if (structure === 'colosseum') return buildColosseumSpecs();
  if (structure === 'city') return buildCitySpecs();
  if (structure === 'cascade') return buildCascadeSpecs();
  return buildTowerSpecs();
}

/** Read-only deterministic layout surface for focused tests and tooling. */
export function getYardStructureSpecs(structure: YardStructure): ReadonlyArray<BrickSpec> {
  return structureSpecs(structure);
}

function buildStructure(rt: YardRuntime, structure: YardStructure) {
  const specs = structureSpecs(structure).slice(0, rt.bodyLimit);
  // Cascade rubble needs high friction / near-zero bounce so the wave can
  // re-sleep after the domino run (otherwise piles grind forever under contact).
  const brickFriction = structure === 'cascade' ? 0.94 : 0.78;
  const brickRestitution = structure === 'cascade' ? 0.01 : 0.04;
  rt.brickBodies = specs.map((spec) => {
    const bodyId = rt.world.createBody({
      type: 'dynamic',
      position: [spec.x, spec.y, spec.z],
      rotation: [0, spec.qy, 0, spec.qw],
    });
    rt.world.addBox(bodyId, [BRICK_FULL.x / 2, BRICK_FULL.y / 2, BRICK_FULL.z / 2], {
      density: 3,
      friction: brickFriction,
      restitution: brickRestitution,
    });
    return bodyId;
  });
  rt.brickColors = specs.map((spec) => spec.tint);
  rt.structure = structure;
  rt.structureVersion += 1;
  rt.idsDirty = true;

  // Force the fresh structure asleep: let the solver establish contacts for a
  // couple of fixed steps, then put every brick to sleep so the page idles at
  // awake≈0 / step<1ms immediately instead of waiting ~5-8s for auto-sleep.
  // Impacts (ball, cannonball, boom impulses) wake bricks normally.
  if (rt.caps.setAwake) {
    rt.world.step(YARD_FIXED_DT, YARD_SUBSTEPS);
    rt.world.step(YARD_FIXED_DT, YARD_SUBSTEPS);
    rt.brickBodies.forEach((bodyId) => rt.world.sleepBody(bodyId as BodyHandle));
  }
}

function destroyDynamicScenery(rt: YardRuntime, nextStructure: YardStructure = rt.structure) {
  endDrag(rt);
  // Restore the connected chain/ball before deleting and rebuilding bricks;
  // otherwise a displaced hero ball can immediately smash the fresh structure.
  restoreWreckingBallPose(rt, nextStructure);
  rt.brickBodies.forEach((bodyId) => rt.world.destroyBody(bodyId as BodyHandle));
  rt.brickBodies = [];
  rt.brickColors = [];
  rt.cannonballs.forEach((bodyId) => rt.world.destroyBody(bodyId as BodyHandle));
  rt.cannonballs = [];
  rt.impacts.length = 0;
  rt.idsDirty = true;
}

export function setYardStructure(rt: YardRuntime, structure: YardStructure) {
  destroyDynamicScenery(rt, structure);
  buildStructure(rt, structure);
}

export function resetYard(rt: YardRuntime) {
  destroyDynamicScenery(rt, rt.structure);
  buildStructure(rt, rt.structure);
}

// --- tools ---

export function spawnCannonball(
  rt: YardRuntime,
  origin: { x: number; y: number; z: number },
  dir: { x: number; y: number; z: number },
) {
  const len = Math.hypot(dir.x, dir.y, dir.z) || 1;
  // Contact events arrive after the collision step. If the target brick is a
  // retired static at that point, the cannonball has already bounced off an
  // immovable wall and the restored dynamic brick receives no useful impulse.
  // Restore only the narrow ballistic corridor before spawning the shot so the
  // first collision transfers momentum normally without reviving all rubble.
  wakeRetiredBricksAlongCannonRay(rt, origin, dir);
  // No body damping on the shot — honest ballistic arc from camera origin.
  const bodyId = rt.world.createBody({
    type: 'dynamic',
    position: [origin.x, origin.y, origin.z],
    ccd: true,
    linearDamping: 0,
    angularDamping: 0.02,
  });
  // High density + near-zero restitution: punches masonry, no pinball ricochet.
  rt.world.addSphere(bodyId, CANNONBALL_RADIUS, {
    density: CANNONBALL_DENSITY,
    friction: 0.55,
    restitution: 0.02,
  });
  rt.world.setLinearVelocity(bodyId, [
    (dir.x / len) * CANNONBALL_SPEED,
    (dir.y / len) * CANNONBALL_SPEED,
    (dir.z / len) * CANNONBALL_SPEED,
  ]);
  rt.cannonballs.push(bodyId);
  while (rt.cannonballs.length > MAX_CANNONBALLS) {
    const oldest = rt.cannonballs.shift();
    if (oldest) rt.world.destroyBody(oldest as BodyHandle);
  }
  rt.idsDirty = true;
}

/** Reset chain peak speed/displacement counters (call before a probe shot). */
export function resetChainStability(rt: YardRuntime) {
  rt.chainPeakSpeed = 0;
  rt.chainPeakDisp = 0;
  rt.chainSampleFrames = 0;
  rt.chainLastMaxSpeed = 0;
}

export interface ChainStabilitySnapshot {
  peakSpeed: number;
  peakDisp: number;
  lastMaxSpeed: number;
  sampleFrames: number;
  calm: boolean;
}

/** Read peak/current chain stability metrics for DEV probes and capture scripts. */
export function getChainStability(rt: YardRuntime): ChainStabilitySnapshot {
  return {
    peakSpeed: rt.chainPeakSpeed,
    peakDisp: rt.chainPeakDisp,
    lastMaxSpeed: rt.chainLastMaxSpeed,
    sampleFrames: rt.chainSampleFrames,
    calm: rt.chainLastMaxSpeed < 1 && rt.chainSampleFrames > 0,
  };
}

function sampleChainStability(rt: YardRuntime) {
  if (rt.chainBodies.length === 0) return;
  let maxSpeed = 0;
  let maxDisp = 0;
  const velOut = { x: 0, y: 0, z: 0 };
  for (let i = 0; i < rt.chainBodies.length; i += 1) {
    const bodyId = rt.chainBodies[i] as BodyHandle;
    if (rt.caps.getLinearVelocity) {
      const v = rt.world.getLinearVelocity(bodyId, velOut);
      const speed = Math.hypot(v.x, v.y, v.z);
      if (speed > maxSpeed) maxSpeed = speed;
    }
    const index = rt.bodyIndex.get(rt.chainBodies[i]);
    if (index != null && rt.prevValid) {
      const offset = index * 7;
      const dx = rt.transforms[offset] - rt.prevTransforms[offset];
      const dy = rt.transforms[offset + 1] - rt.prevTransforms[offset + 1];
      const dz = rt.transforms[offset + 2] - rt.prevTransforms[offset + 2];
      const disp = Math.hypot(dx, dy, dz);
      if (disp > maxDisp) maxDisp = disp;
    }
  }
  // Fallback speed from displacement when velocity API is missing.
  if (!rt.caps.getLinearVelocity && rt.prevValid) {
    maxSpeed = maxDisp / YARD_FIXED_DT;
  }
  rt.chainLastMaxSpeed = maxSpeed;
  rt.chainSampleFrames += 1;
  if (maxSpeed > rt.chainPeakSpeed) rt.chainPeakSpeed = maxSpeed;
  if (maxDisp > rt.chainPeakDisp) rt.chainPeakDisp = maxDisp;
}

export function boomAt(rt: YardRuntime, x: number, y: number, z: number): { usedNative: boolean } {
  // Phase 2: demote retired statics in radius to dynamic BEFORE impulse so
  // radialImpulse can actually move them (statics ignore impulse).
  wakeRetiredBricksInRadius(rt, x, y, z, BOOM_RADIUS);
  // Radial impulses on bricks within BOOM_RADIUS, using the last read
  // transforms (positions are at most one frame stale). applyImpulse reliably
  // wakes sleeping bricks, which the native explode path does not on a settled
  // stack. The library's radialImpulse helper reproduces the old boomAt math:
  // strength 0.9, quadratic falloff, upwardBias 0.28.
  radialImpulse(rt.world, rt.brickBodies as BodyHandle[], rt.buffer, {
    center: [x, y, z],
    radius: BOOM_RADIUS,
    strength: 0.9,
    falloff: 'quadratic',
    upwardBias: 0.28,
  });
  return { usedNative: false };
}

/**
 * Scripted wrecking-ball strike for the Wake Wave demo: aim the ball at tower 1's
 * upper section.
 *
 * Immediately before velocity is applied, ball + chain are teleported to a
 * deterministic clear swing pose ({@link cascadeLaunchBallPose}) via setBodyTransform.
 * That drops settle-time contact with non-target towers (tower 10 overlaps the
 * rest sphere by ~14 cm) so scalar/SIMD launch outcomes no longer depend on
 * hidden contact state or click timing. Bricks are never teleported or directly
 * impulsed — pure physics contact drives the cascade strike on tower 1.
 */
export function cascadeLaunch(rt: YardRuntime) {
  const tower1 = CASCADE_TOWER_CENTERS[0];
  if (!tower1 || !rt.ballBodyId) return;

  // Clear unintended tower contacts + settle drift before applying launch velocity.
  prepareCascadeLaunchPose(rt);

  // Aim from the deterministic launch pose (not the stale transform buffer).
  const ball = cascadeLaunchBallPose();
  // Aim at the upper half of tower 1 (ball rest bottom ~y 6.9; tower top ~6.5).
  const aimY = cascadeTowerHeight() * 0.72;
  const dx = tower1.x - ball.x;
  const dy = aimY - ball.y;
  const dz = tower1.z - ball.z;
  // Prefer a mostly-horizontal strike; small downward bias helps contact mid-swing.
  const horiz = Math.hypot(dx, dz) || 1;
  const vx = (dx / horiz) * CASCADE_LAUNCH_SPEED;
  const vz = (dz / horiz) * CASCADE_LAUNCH_SPEED;
  const vy = Math.min(0, dy * 0.15);

  if (rt.caps.setAwake) {
    rt.world.setAwake(rt.ballBodyId as BodyHandle, true);
    rt.chainBodies.forEach((bodyId) => rt.world.setAwake(bodyId as BodyHandle, true));
  }
  rt.world.setLinearVelocity(rt.ballBodyId as BodyHandle, [vx, vy, vz]);
  // Light chain co-velocity so links don't instantly cancel the ball impulse.
  // Keep this modest — high co-velocity on heavy links catapults past the anchor.
  const chainScale = 0.35;
  rt.chainBodies.forEach((bodyId) => {
    rt.world.setLinearVelocity(bodyId as BodyHandle, [vx * chainScale, vy * chainScale, vz * chainScale]);
  });
}

/** Quiet the wrecking ball + chain so residual swing doesn't keep stirring rubble. */
export function calmWreckingBall(rt: YardRuntime) {
  rt.chainBodies.forEach((bodyId) => {
    rt.world.setLinearVelocity(bodyId as BodyHandle, [0, 0, 0]);
    rt.world.setAngularVelocity(bodyId as BodyHandle, [0, 0, 0]);
    if (rt.caps.setAwake) rt.world.sleepBody(bodyId as BodyHandle);
  });
  if (rt.ballBodyId) {
    rt.world.setLinearVelocity(rt.ballBodyId as BodyHandle, [0, 0, 0]);
    rt.world.setAngularVelocity(rt.ballBodyId as BodyHandle, [0, 0, 0]);
    if (rt.caps.setAwake) rt.world.sleepBody(rt.ballBodyId as BodyHandle);
  }
}

/**
 * Force-sleep cascade bricks that have stopped moving. Used after the wake wave
 * so the city returns to the blue (asleep) heatmap without waiting on slow
 * contact-island deactivation of a multi-thousand-body rubble pile.
 */
export function sleepSettledCascadeBricks(rt: YardRuntime) {
  if (rt.structure !== 'cascade' || !rt.caps.setAwake || !rt.prevValid) return;
  const eps = 0.012;
  // Never soft-freeze airborne rubble mid-flight — only ground/pile bricks.
  const groundY = 1.35;
  for (let i = 0; i < rt.brickBodies.length; i += 1) {
    // Retired statics are already out of the dynamic island — skip sleep work.
    if (rt.brickRetired[i] === 1) continue;
    if (rt.brickSleepStates[i] === 0) continue;
    const offset = i * 7;
    if (rt.transforms[offset + 1] > groundY) continue;
    const dx = Math.abs(rt.transforms[offset] - rt.prevTransforms[offset]);
    const dy = Math.abs(rt.transforms[offset + 1] - rt.prevTransforms[offset + 1]);
    const dz = Math.abs(rt.transforms[offset + 2] - rt.prevTransforms[offset + 2]);
    if (dx + dy + dz <= eps) {
      const bodyId = rt.brickBodies[i] as BodyHandle;
      rt.world.setLinearVelocity(bodyId, [0, 0, 0]);
      rt.world.setAngularVelocity(bodyId, [0, 0, 0]);
      rt.world.sleepBody(bodyId);
    }
  }
}

/**
 * Count cascade bricks still airborne and moving — used to gate blanket-sleep so
 * rubble is not frozen mid-air when the city snaps back to blue.
 */
export function countAirborneMovingCascadeBricks(rt: YardRuntime): number {
  if (rt.structure !== 'cascade' || !rt.prevValid) return rt.brickBodies.length;
  let n = 0;
  const airY = 1.15;
  const moveEps = 0.018;
  for (let i = 0; i < rt.brickBodies.length; i += 1) {
    const offset = i * 7;
    const y = rt.transforms[offset + 1];
    if (y < airY) continue;
    const dx = Math.abs(rt.transforms[offset] - rt.prevTransforms[offset]);
    const dy = Math.abs(rt.transforms[offset + 1] - rt.prevTransforms[offset + 1]);
    const dz = Math.abs(rt.transforms[offset + 2] - rt.prevTransforms[offset + 2]);
    if (dx + dy + dz > moveEps) n += 1;
  }
  return n;
}

/**
 * Sleep cascade bricks. Default (hard=false): only settled / ground-pile bricks so
 * airborne rubble is not frozen mid-flight for the showcase clip. hard=true:
 * zero+sleep everything (used after airborne has landed, or as a late safety net).
 */
export function forceSleepAllCascadeBricks(rt: YardRuntime, opts: { hard?: boolean } = {}) {
  if (rt.structure !== 'cascade' || !rt.caps.setAwake) return;
  const hard = opts.hard === true;
  const hasPrev = rt.prevValid;
  const stillEps = 0.02;
  const groundY = 1.35;
  for (let i = 0; i < rt.brickBodies.length; i += 1) {
    // Skip retired statics (already non-dynamic; sleepBody would be a no-op).
    if (rt.brickRetired[i] === 1) continue;
    const bodyId = rt.brickBodies[i] as BodyHandle;
    if (!hard) {
      const offset = i * 7;
      const y = rt.transforms[offset + 1];
      // Soft path is ground-pile only — never freeze airborne rubble (even if
      // briefly still at apex), so the showcase never shows mid-air blue bricks.
      if (y > groundY) continue;
      let disp = 0;
      if (hasPrev) {
        disp =
          Math.abs(rt.transforms[offset] - rt.prevTransforms[offset]) +
          Math.abs(rt.transforms[offset + 1] - rt.prevTransforms[offset + 1]) +
          Math.abs(rt.transforms[offset + 2] - rt.prevTransforms[offset + 2]);
      }
      const still = !hasPrev || disp <= stillEps;
      if (!still && disp > stillEps * 2.5) continue;
    }
    rt.world.setLinearVelocity(bodyId, [0, 0, 0]);
    rt.world.setAngularVelocity(bodyId, [0, 0, 0]);
    rt.world.sleepBody(bodyId);
  }
}

/** True when almost no cascade bricks are still airborne-and-moving. */
export function cascadeRubbleReadyToSleep(rt: YardRuntime): boolean {
  return countAirborneMovingCascadeBricks(rt) <= 20;
}

export function brickBodyIdAt(rt: YardRuntime, index: number): number {
  return rt.brickBodies[index] ?? 0;
}

export function bodyPosition(rt: YardRuntime, bodyId: number): { x: number; y: number; z: number } | null {
  const index = rt.bodyIndex.get(bodyId);
  if (index == null) return null;
  const offset = index * 7;
  return { x: rt.transforms[offset], y: rt.transforms[offset + 1], z: rt.transforms[offset + 2] };
}

export function ballPosition(rt: YardRuntime): { x: number; y: number; z: number } {
  return (
    bodyPosition(rt, rt.ballBodyId) ?? {
      x: CHAIN_ANCHOR.x,
      y: CHAIN_ANCHOR.y - CHAIN_LINKS * CHAIN_SPACING - BALL_RADIUS,
      z: CHAIN_ANCHOR.z,
    }
  );
}

export function beginDrag(rt: YardRuntime, targetBodyId: number, px: number, py: number, pz: number) {
  endDrag(rt);
  // Phase 2: if grabbing a retired static brick, demote to dynamic first so the
  // spherical joint can actually move it.
  const brickIdx = brickIndexOfBody(rt, targetBodyId);
  if (brickIdx >= 0) wakeRetiredBrick(rt, brickIdx);
  // A kinematic "hand" with a tiny sensor shape (sensors never collide
  // physically) is jointed to the target and driven each fixed step via
  // setKinematicTarget, so momentum transfers through the joint and release
  // keeps the velocity. The library now exposes destroyJoint, so the hand body
  // is created once and reused; only the joint is created per drag and torn
  // down on release (retires the old destroy-the-hand-body hack).
  if (!rt.handBodyId) {
    const hand = rt.world.createBody({ type: 'kinematic', position: [px, py, pz] });
    rt.world.addSensorBox(hand, [0.05, 0.05, 0.05]);
    rt.handBodyId = hand;
  } else {
    rt.world.setBodyTransform(rt.handBodyId as BodyHandle, [px, py, pz], [0, 0, 0, 1]);
  }
  rt.dragJoint = rt.world.createSphericalJoint(rt.handBodyId as BodyHandle, targetBodyId as BodyHandle, {
    anchor: [px, py, pz],
  });
  rt.handPos = { x: px, y: py, z: pz };
  rt.dragTarget = { x: px, y: py, z: pz };
  rt.dragging = true;
}

export function updateDragTarget(rt: YardRuntime, x: number, y: number, z: number) {
  if (!rt.dragging) return;
  rt.dragTarget = { x, y, z };
}

export function endDrag(rt: YardRuntime) {
  if (!rt.dragging && !rt.dragJoint) return;
  if (rt.dragJoint) {
    rt.world.destroyJoint(rt.dragJoint);
    rt.dragJoint = 0;
  }
  // Park the reusable hand body far below the scene so its idle sensor never
  // interacts; it is destroyed only when the world is torn down.
  if (rt.handBodyId) {
    rt.world.setBodyTransform(rt.handBodyId as BodyHandle, [0, -900, 0], [0, 0, 0, 1]);
  }
  rt.dragging = false;
}

function driveHand(rt: YardRuntime, dt: number) {
  if (!rt.dragging || !rt.handBodyId) return;
  const dx = rt.dragTarget.x - rt.handPos.x;
  const dy = rt.dragTarget.y - rt.handPos.y;
  const dz = rt.dragTarget.z - rt.handPos.z;
  const d = Math.hypot(dx, dy, dz);
  const maxStep = HAND_MAX_SPEED * dt;
  const s = d > maxStep ? maxStep / d : 1;
  rt.handPos = {
    x: rt.handPos.x + dx * s,
    y: rt.handPos.y + dy * s,
    z: rt.handPos.z + dz * s,
  };
  rt.world.setKinematicTarget(rt.handBodyId as BodyHandle, [rt.handPos.x, rt.handPos.y, rt.handPos.z], [0, 0, 0, 1], dt);
}

// --- frame advance ---

/**
 * Advances yard physics; returns fixed-step count from FixedStepper.advance (0 when none).
 * @param cascadeOptimizationActive — true only while cascade lifecycle is active.
 *   Coverage is sticky, so this flag scopes aggressive maxSteps ({4,1}) to the
 *   live cascade and restores proven {4,3} for post-cascade cannon/re-settle.
 *   Also blocks Phase 2 debris retirement while true (no base-welding mid-wave).
 */
export function advanceYardPhysics(
  rt: YardRuntime,
  delta: number,
  cascadeOptimizationActive = false,
): number {
  const scale = rt.slowMo ? 0.25 : 1;
  // Phase 1a/1b: policy from previous frame's awake bricks, last stepMs,
  // sticky ever-awake coverage (product wake gate), and cascade lifecycle.
  // Substeps are always 4 (box3d contact-solver iterations). Only maxSteps
  // adapts; after cascade ends, sticky coverage alone must not keep a reduced
  // catch-up budget (measured post-cascade 3625-awake micro-jitter deadlock).
  // setMaxStepsPerFrame only mutates the budget (accumulator/simTime preserved).
  const wakeCoverageSatisfied = isYardWakeCoverageSatisfied(
    rt.everAwakeCount,
    rt.brickBodies.length,
  );
  const budget = selectYardStepBudget(
    rt.awakeBrickCount,
    rt.stepMs,
    wakeCoverageSatisfied,
    cascadeOptimizationActive,
  );
  rt.stepper.setMaxStepsPerFrame(budget.maxStepsPerFrame);
  rt.substepsUsed = budget.substeps;
  rt.maxStepsUsed = budget.maxStepsPerFrame;
  let stepMsTotal = 0;
  const stepsThisFrame = rt.stepper.advance(delta * scale, () => {
    driveHand(rt, YARD_FIXED_DT);
    const t0 = performance.now();
    rt.world.step(YARD_FIXED_DT, budget.substeps);
    stepMsTotal += performance.now() - t0;
    rt.fixedTime += YARD_FIXED_DT;
  });
  if (stepsThisFrame) {
    rt.stepMs = stepMsTotal;
  }

  // Contact drain also drives Phase 2 wake-back (actor / high-speed hits only).
  collectImpacts(rt);
  if (rt.dragging) {
    // Keep the sensor event buffer drained while the hand sensor exists.
    rt.world.drainSensorEvents();
  }

  if (rt.idsDirty) {
    rebuildBuffers(rt);
  }

  if (rt.bodyIds.length > 0) {
    if (stepsThisFrame && rt.prevValid) {
      rt.prevTransforms.set(rt.transforms);
    }
    rt.buffer.readInto(rt.world);
    readBrickSleepStates(rt);
    if (stepsThisFrame) {
      sampleChainStability(rt);
      // Phase 2: retire grounded engine-asleep cascade debris only after the
      // cascade lifecycle ends (no dwell/retirement while optimization active).
      // Uses prevTransforms vs transforms (already available — no extra full pose scan).
      retireSettledCascadeBricks(rt, stepsThisFrame * YARD_FIXED_DT, cascadeOptimizationActive);
      computeAwake(rt);
      rt.sleep.forceSleepSettled();
      rt.sleep.sweep(rt.stepper.simTime);
      rt.prevValid = true;
    }
  }

  rt.bodyCountTotal = rt.caps.bodyCount
    ? rt.world.bodyCount()
    : rt.staticBodyCount +
      rt.brickBodies.length +
      rt.chainBodies.length +
      1 +
      rt.cannonballs.length +
      (rt.handBodyId ? 1 : 0);

  return stepsThisFrame;
}

function readBrickSleepStates(rt: YardRuntime) {
  if (rt.brickBodyIds.length === 0) {
    rt.awakeBrickCount = 0;
    return;
  }
  rt.world.readSleepStates(rt.brickBodyIds, rt.brickSleepStates);
  // Piggyback awake brick count on this pass (Phase 1 policy input; no second scan).
  // Sticky ever-awake coverage for cascade / sleep-demo metrics.
  // Phase 2: retired statics always read as asleep (blue heatmap), never clear everAwake.
  const n = rt.brickSleepStates.length;
  const trackEver = rt.brickEverAwake.length === n;
  const trackRetired = rt.brickRetired.length === n;
  let awake = 0;
  for (let i = 0; i < n; i += 1) {
    if (trackRetired && rt.brickRetired[i] === 1) {
      rt.brickSleepStates[i] = 0;
      continue;
    }
    if (rt.brickSleepStates[i] === 1) {
      awake += 1;
      if (trackEver && rt.brickEverAwake[i] === 0) {
        rt.brickEverAwake[i] = 1;
        rt.everAwakeCount += 1;
      }
    }
  }
  rt.awakeBrickCount = awake;
}

function computeAwake(rt: YardRuntime) {
  if (rt.caps.awakeBodyCount) {
    rt.awakeCount = rt.world.awakeBodyCount();
    return;
  }
  if (!rt.prevValid) {
    rt.awakeCount = rt.bodyIds.length;
    return;
  }
  // Fallback: count bodies whose position moved more than epsilon since the
  // previous stepped frame.
  let awake = 0;
  const eps = 0.0006;
  for (let i = 0; i < rt.bodyIds.length; i += 1) {
    const offset = i * 7;
    const dx = Math.abs(rt.transforms[offset] - rt.prevTransforms[offset]);
    const dy = Math.abs(rt.transforms[offset + 1] - rt.prevTransforms[offset + 1]);
    const dz = Math.abs(rt.transforms[offset + 2] - rt.prevTransforms[offset + 2]);
    if (dx + dy + dz > eps) awake += 1;
  }
  rt.awakeCount = awake;
}

function collectImpacts(rt: YardRuntime) {
  const events = rt.world.drainContactBeginEvents();
  for (const event of events) {
    // Phase 2 wake-back before impact FX filter (actors may wake at low speed).
    processContactWakeBack(rt, event.bodyA, event.bodyB, event.approachSpeed);
    if (event.approachSpeed < 2.2) continue;
    const pos = bodyPosition(rt, event.bodyA) ?? bodyPosition(rt, event.bodyB);
    if (!pos) continue;
    if (rt.impacts.length < 8) {
      rt.impacts.push({ x: pos.x, y: pos.y, z: pos.z, strength: event.approachSpeed });
    }
  }
}

// --- Phase 2 retirement / wake-back helpers ---------------------------------

/** Brick index for a body id, or -1 if not a tracked cascade/structure brick. */
function brickIndexOfBody(rt: YardRuntime, bodyId: number): number {
  const idx = rt.bodyIndex.get(bodyId);
  if (idx == null || idx >= rt.brickBodies.length) return -1;
  return idx;
}

/** Wrecking-ball, chain links, or live cannonballs — always-wake contact actors. */
function isYardImpulseActor(rt: YardRuntime, bodyId: number): boolean {
  if (bodyId === rt.ballBodyId) return true;
  for (let i = 0; i < rt.chainBodies.length; i += 1) {
    if (rt.chainBodies[i] === bodyId) return true;
  }
  for (let i = 0; i < rt.cannonballs.length; i += 1) {
    if (rt.cannonballs[i] === bodyId) return true;
  }
  return false;
}

function syncDynamicBrickCount(rt: YardRuntime) {
  rt.dynamicBrickCount = Math.max(0, rt.brickBodies.length - rt.retiredCount);
}

/**
 * Static → dynamic for a single retired brick. Sets wake-grace dwell so the
 * same-frame retire sweep cannot re-static it. Mass is preserved by setBodyType
 * for density-3 yard bricks (probed). No-op without caps.setBodyType or when not retired.
 */
export function wakeRetiredBrick(rt: YardRuntime, index: number): boolean {
  if (!rt.caps.setBodyType) return false;
  if (index < 0 || index >= rt.brickBodies.length) return false;
  if (rt.brickRetired[index] !== 1) return false;
  const bodyId = rt.brickBodies[index] as BodyHandle;
  rt.world.setBodyType(bodyId, 'dynamic');
  if (rt.caps.setAwake) rt.world.setAwake(bodyId, true);
  rt.brickRetired[index] = 0;
  rt.brickRetireDwell[index] = -YARD_RETIRE_WAKE_GRACE_SEC;
  rt.retiredCount = Math.max(0, rt.retiredCount - 1);
  syncDynamicBrickCount(rt);
  return true;
}

/** Wake every retired brick whose center is within radius of (x,y,z). */
export function wakeRetiredBricksInRadius(
  rt: YardRuntime,
  x: number,
  y: number,
  z: number,
  radius: number,
): number {
  if (!rt.caps.setBodyType || rt.retiredCount === 0) return 0;
  const r2 = radius * radius;
  let n = 0;
  for (let i = 0; i < rt.brickBodies.length; i += 1) {
    if (rt.brickRetired[i] !== 1) continue;
    const offset = i * 7;
    const dx = rt.transforms[offset] - x;
    const dy = rt.transforms[offset + 1] - y;
    const dz = rt.transforms[offset + 2] - z;
    if (dx * dx + dy * dy + dz * dz <= r2) {
      if (wakeRetiredBrick(rt, i)) n += 1;
    }
  }
  return n;
}

/** Pure geometry check used by cannon pre-wake and its regression tests. */
export function isPointInCannonWakeCorridor(
  point: { x: number; y: number; z: number },
  origin: { x: number; y: number; z: number },
  dir: { x: number; y: number; z: number },
  length = CANNON_WAKE_CORRIDOR_LENGTH,
  radius = CANNON_WAKE_CORRIDOR_RADIUS,
): boolean {
  const dirLength = Math.hypot(dir.x, dir.y, dir.z);
  if (dirLength <= Number.EPSILON || length <= 0 || radius < 0) return false;

  const nx = dir.x / dirLength;
  const ny = dir.y / dirLength;
  const nz = dir.z / dirLength;
  const rx = point.x - origin.x;
  const ry = point.y - origin.y;
  const rz = point.z - origin.z;
  const along = rx * nx + ry * ny + rz * nz;
  if (along < 0 || along > length) return false;

  const perpendicularSq = Math.max(0, rx * rx + ry * ry + rz * rz - along * along);
  return perpendicularSq <= radius * radius;
}

/**
 * Static → dynamic for retired bricks the cannonball can actually intersect.
 * This runs before the projectile body exists so the first contact is solved
 * against movable masonry instead of a temporary static wall.
 */
export function wakeRetiredBricksAlongCannonRay(
  rt: YardRuntime,
  origin: { x: number; y: number; z: number },
  dir: { x: number; y: number; z: number },
): number {
  if (!rt.caps.setBodyType || rt.retiredCount === 0) return 0;
  let woke = 0;
  for (let i = 0; i < rt.brickBodies.length; i += 1) {
    if (rt.brickRetired[i] !== 1) continue;
    const offset = i * 7;
    const point = {
      x: rt.transforms[offset],
      y: rt.transforms[offset + 1],
      z: rt.transforms[offset + 2],
    };
    if (isPointInCannonWakeCorridor(point, origin, dir) && wakeRetiredBrick(rt, i)) {
      woke += 1;
    }
  }
  return woke;
}

function processContactWakeBack(
  rt: YardRuntime,
  bodyA: number,
  bodyB: number,
  approachSpeed: number,
) {
  if (!rt.caps.setBodyType || rt.structure !== 'cascade' || rt.retiredCount === 0) return;
  const iA = brickIndexOfBody(rt, bodyA);
  const iB = brickIndexOfBody(rt, bodyB);
  const actorA = isYardImpulseActor(rt, bodyA);
  const actorB = isYardImpulseActor(rt, bodyB);
  // Still-dynamic brick partner (not retired). Actors are handled via otherIsActor.
  const dynBrickA = iA >= 0 && rt.brickRetired[iA] !== 1;
  const dynBrickB = iB >= 0 && rt.brickRetired[iB] !== 1;

  if (
    iA >= 0 &&
    shouldWakeRetiredOnContact({
      brickRetired: rt.brickRetired[iA] === 1,
      otherIsActor: actorB,
      otherIsDynamicPartner: dynBrickB,
      approachSpeed,
    })
  ) {
    wakeRetiredBrick(rt, iA);
  }
  if (
    iB >= 0 &&
    shouldWakeRetiredOnContact({
      brickRetired: rt.brickRetired[iB] === 1,
      otherIsActor: actorA,
      otherIsDynamicPartner: dynBrickA,
      approachSpeed,
    })
  ) {
    wakeRetiredBrick(rt, iB);
  }
}

/**
 * Phase 2 ground-only retire sweep (post-cascade only):
 *   cascadeOptimizationActive → no-op (no dwell, no retirement mid-wave)
 *   engine-awake → tier none (sleep is the stillness gate)
 *   ground + engine-asleep → 1.25s rubble dwell then static
 *   elevated → blocked; displacement alone cannot prove lasting support
 * O(n) over bricks once per stepped frame; reuses transforms / prevTransforms.
 * brickSleepStates is populated by readBrickSleepStates earlier in the same advance.
 */
export function retireSettledCascadeBricks(
  rt: YardRuntime,
  dt: number,
  cascadeOptimizationActive = false,
) {
  // Never retire (or accumulate dwell) while the cascade lifecycle / post-tool
  // window is active — mid-wave static-pin of lower layers (y ≤ 1.35) welds
  // tower bases before the collapse reaches them.
  if (cascadeOptimizationActive) return;
  if (rt.structure !== 'cascade' || !rt.caps.setBodyType) return;
  if (!rt.prevValid || dt <= 0) return;
  if (rt.brickRetired.length !== rt.brickBodies.length) return;

  const brickCount = rt.brickBodies.length;
  const everAwake = rt.brickEverAwake;
  const sleepStates = rt.brickSleepStates;

  for (let i = 0; i < brickCount; i += 1) {
    if (rt.brickRetired[i] === 1) continue;

    const offset = i * 7;
    const y = rt.transforms[offset + 1];
    const disp =
      Math.abs(rt.transforms[offset] - rt.prevTransforms[offset]) +
      Math.abs(rt.transforms[offset + 1] - rt.prevTransforms[offset + 1]) +
      Math.abs(rt.transforms[offset + 2] - rt.prevTransforms[offset + 2]);
    const touched = everAwake.length === brickCount && everAwake[i] === 1;
    // brickSleepStates: 1 = engine-awake, 0 = engine-asleep (read earlier this advance).
    const asleep = sleepStates.length === brickCount && sleepStates[i] === 0;
    // Safety gate blocks every elevated body; classifyRetireTier forces none when awake.
    const tier = gateRetireTier(
      classifyRetireTier(y, disp, touched, asleep),
      rt.everAwakeCount,
      brickCount,
    );
    const dwell = updateRetireDwell(rt.brickRetireDwell[i], {
      tier,
      retired: false,
      dt,
    });
    rt.brickRetireDwell[i] = dwell;
    if (!shouldRetireFromDwell(dwell, false, tier)) continue;

    const bodyId = rt.brickBodies[i] as BodyHandle;
    rt.world.setLinearVelocity(bodyId, [0, 0, 0]);
    rt.world.setAngularVelocity(bodyId, [0, 0, 0]);
    rt.world.setBodyType(bodyId, 'static');
    rt.brickRetired[i] = 1;
    rt.brickRetireDwell[i] = 0;
    rt.brickSleepStates[i] = 0;
    rt.retiredCount += 1;
  }
  syncDynamicBrickCount(rt);
}

function rebuildBuffers(rt: YardRuntime) {
  const ids: number[] = [];
  rt.brickBodies.forEach((bodyId) => ids.push(bodyId));
  rt.chainBodies.forEach((bodyId) => ids.push(bodyId));
  ids.push(rt.ballBodyId);
  rt.cannonballs.forEach((bodyId) => ids.push(bodyId));

  // Rebuild the TransformBuffer's tracked set in
  // [bricks..., chain..., ball, cannonballs...] insertion order so the flat
  // rt.transforms offsets the render layer relies on stay stable.
  rt.buffer = new TransformBuffer(Math.max(ids.length, 1));
  for (const bodyId of ids) rt.buffer.add(bodyId as BodyHandle);
  rt.buffer.rebuild();

  rt.bodyIds = rt.buffer.ids;
  // Capture previous brick handles BEFORE overwriting brickBodyIds — same-count
  // rebuilds often mint N new IDs and must not inherit stale coverage/retirement.
  const prevBrickBodyIds = rt.brickBodyIds;
  const prevEver = rt.brickEverAwake;
  const prevEverCount = rt.everAwakeCount;
  const prevRetired = rt.brickRetired;
  const prevDwell = rt.brickRetireDwell;
  const prevRetiredCount = rt.retiredCount;
  const sameBricks = yardBrickSetUnchanged(prevBrickBodyIds, rt.brickBodies);
  rt.brickBodyIds = Int32Array.from(rt.brickBodies);
  const brickN = rt.brickBodyIds.length;
  if (rt.brickSleepStates.length !== brickN) {
    rt.brickSleepStates = new Uint8Array(brickN);
    rt.awakeBrickCount = 0;
  }
  // Preserve sticky ever-awake + retirement only when every brick handle/order
  // is identical (e.g. cannonball churn). Structure reset/rebuild with the same
  // brick count but new body IDs must fully reset coverage and retirement state.
  if (sameBricks && prevEver.length === brickN) {
    rt.brickEverAwake = prevEver;
    rt.everAwakeCount = prevEverCount;
    if (prevRetired.length === brickN && prevDwell.length === brickN) {
      rt.brickRetired = prevRetired;
      rt.brickRetireDwell = prevDwell;
      rt.retiredCount = prevRetiredCount;
    } else {
      rt.brickRetired = new Uint8Array(brickN);
      rt.brickRetireDwell = new Float32Array(brickN);
      rt.retiredCount = 0;
    }
  } else {
    rt.brickEverAwake = new Uint8Array(brickN);
    rt.everAwakeCount = 0;
    rt.awakeBrickCount = 0;
    rt.brickRetired = new Uint8Array(brickN);
    rt.brickRetireDwell = new Float32Array(brickN);
    rt.retiredCount = 0;
  }
  syncDynamicBrickCount(rt);
  rt.transforms = rt.buffer.transforms;
  rt.prevTransforms = new Float32Array(ids.length * 7);
  rt.prevValid = false;
  rt.bodyIndex = new Map(ids.map((bodyId, index) => [bodyId, index]));

  // Re-arm the sleep discipline for the current brick set (bricks only; the
  // chain, hero ball and cannonballs are meant to keep swinging / rolling).
  // Library SleepManager watches every brick handle — including retired statics
  // (sleepBody on static is a harmless no-op). App soft/hard sleep loops skip
  // retired indices; they do not reconfigure the library watch list.
  rt.sleep.watch(rt.brickBodies as BodyHandle[], rt.buffer);
  rt.idsDirty = false;
}

// Buffer layout helpers for the render layer: [bricks..., chain..., ball, cannonballs...]
export function brickTransformOffset(index: number) {
  return index * 7;
}

export function chainTransformOffset(rt: YardRuntime, link: number) {
  return (rt.brickBodies.length + link) * 7;
}

export function ballTransformOffset(rt: YardRuntime) {
  return (rt.brickBodies.length + rt.chainBodies.length) * 7;
}

export function cannonballTransformOffset(rt: YardRuntime, index: number) {
  return (rt.brickBodies.length + rt.chainBodies.length + 1 + index) * 7;
}
